/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { useEffect, useState } from 'react';
import { Autocomplete, Button, Grid, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { format, getMonth, getYear, lastDayOfMonth } from 'date-fns';
import { DataGrid } from '@mui/x-data-grid';
import { useDispatch, useSelector } from 'react-redux';
import styles from 'modules/dashboard/components/tab-container/workbook-report/workbook-report.module.scss';
import {
  selectHotelName,
  selectIsDemoModeActivated,
  selectLocalCurrency,
  selectTabList,
} from 'modules/dashboard/selectors';
import { formatDate } from 'modules/dashboard/functions';
import { selectAuthUser, selectOrganizationId } from 'modules/common/auth/selectors';
import { Loader } from 'modules/common/components';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import NoDataDialog from 'modules/dashboard/components/tab-container/export-visual-data/components/no-data';
import { selectLoader, selectIataData } from '../../../../selectors';
import { workbookActions } from '../../../../slice';
import { buildNegConIataFilters } from '../../../../function';
import { DATE_TIME_WITH_AM_PM_PATTERN } from '../../../../../../../../common/constants/date-range';
import {
  buildHeaderMap,
  generateExcelAndDownload,
  getPastNPeriods,
  getPeriods,
  isEmptyObject,
  mapVisualsToRows,
} from './functions';
import { FILE_FORMATS, MONTHS } from './constants';
import useExportWidget from '../../../../hooks/use-export-widget';
import { EXPORT_CERULEAN_COLUMN_MAPPER } from '../../../../constants';
//
const ExportWorkbookReport = ({ value, active }) => {
  const dispatch = useDispatch();
  //
  useExportWidget();
  //
  const loading = useSelector(selectLoader);
  const organizationId = useSelector(selectOrganizationId);
  const hotels = useSelector(selectHotelName);
  const currencyType = useSelector(selectLocalCurrency);
  const iataData = useSelector(selectIataData);
  const currentUser = useSelector(selectAuthUser);
  const isDemoMode = useSelector(selectIsDemoModeActivated);
  const TAB_VALUE = useSelector(selectTabList);
  //
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedDate, setSelectedDate] = useState();
  const [selectedRow, setSelectedRow] = useState();
  const [openNoDataDialog, setOpenNoDataDialog] = useState(false);
  //
  const [selectedFilteringReportName, setSelectedFilteringReportName] = useState('');
  const [selectedFilteringYear, setSelectedFilteringYear] = useState('');
  const [selectedFilteringMonth, setSelectedFilteringMonth] = useState('');
  //
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  //
  const handleCloseNoDataDialog = () => setOpenNoDataDialog(false);
  const handleOpenNoDataDialog = () => setOpenNoDataDialog(true);
  // Export logic
  const exportVisual = (row) => {
    const monthIndex = MONTHS.indexOf(row?.month);
    const selectedDateObj = lastDayOfMonth(new Date(Number(row?.year), monthIndex));
    setSelectedDate(format(selectedDateObj, 'yyyy-MM-dd'));
    const filters = buildNegConIataFilters({
      hotels,
      currencyType,
      selectedDate: format(selectedDateObj, 'yyyy-MM-dd'),
      currentYear: Number(row?.year),
      isDemoMode,
    });
    //
    setIsDownloading(true);
    const columns = EXPORT_CERULEAN_COLUMN_MAPPER[row.name];
    setSelectedRow(row);
    dispatch(
      workbookActions.getIataData({
        organizationId,
        columns,
        filters,
      })
    );
  };

  // Download Excel when iataData changes
  useEffect(() => {
    if (!isDownloading) {
      setIsDownloading(false);
      return;
    }
    if (isEmptyObject(iataData)) {
      handleOpenNoDataDialog();
      return;
    }
    //
    const downloadExcel = async () => {
      const selectedDateObj = new Date(selectedDate);
      await generateExcelAndDownload({
        dataRows: Array.isArray(iataData) ? { [selectedRow.label]: iataData } : iataData,
        forceReplaceHeaders: { category: 'Rate Category' },
        infoData: {
          'Username: ': currentUser?.username,
          'Report Generation Date: ': formatDate(new Date(), DATE_TIME_WITH_AM_PM_PATTERN),
          ['hotels' in hotels ? 'Hotel Set: ' : 'Hotel: ']: isDemoMode
            ? hotels?.altName
            : hotels?.label,
          'Selected Date Range: ': `${selectedDateObj.getFullYear()}-01-01 - ${selectedDate}`,
          'Selected Currency: ': currencyType,
        },
        legendData: [
          { type: 'heading', values: buildHeaderMap(iataData, selectedRow.label) },
          { type: 'item', label: 'ADR', description: 'Average Daily Rate' },
          { type: 'item', label: 'ALOS', description: 'Average Length of Stay' },
          ...(selectedRow.name === 'Rates By Channel'
            ? [{ type: 'item', label: 'ALT', description: 'Average Lead Time' }]
            : []),
          {
            type: 'item',
            label: 'Arrivals',
            description: 'Reservations that were consumed in prior years',
          },
          {
            type: 'item',
            label: 'Bookings',
            description:
              'Reservations that have been consumed or are currently on the books for the current year',
          },
        ],
        fileName: `${selectedRow.name?.replaceAll(' ', '_')}_${formatDate(
          new Date(),
          DATE_TIME_WITH_AM_PM_PATTERN
        )}`,
        selectedPeriod: MONTHS[getMonth(new Date(selectedDate))],
      });

      setIsDownloading(false);
    };
    //
    downloadExcel();
  }, [iataData]);
  //
  const start = new Date('2020-01-01');
  const end = new Date();
  const startYear = getYear(start);
  const endYear = getYear(new Date());
  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => endYear - i);
  const periods = getPeriods(start, end);
  const pastThreeMonthsPeriods = getPastNPeriods();
  //
  const visuals = [
    {
      id: '1',
      name: 'NEG-CON-IATA',
      label: 'Top IATA',
      description: 'Top Negotiated and Consortia rates and top IATA, YTD as at end of the month',
    },
    {
      id: '2',
      name: 'IATA by Rate Codes Detailed',
      label: 'Detailed IATA',
      description: 'Detailed rate codes by IATA, YTD as at end of the month, grouped by arrival month',
    },
    {
      id: '3',
      name: 'Rates By Channel',
      label: 'Rates by channel',
      description: 'Detailed rate codes by channel, YTD as at end of month',
    },
  ];
  //
  const rows = mapVisualsToRows(
    visuals,
    periods,
    selectedFilteringReportName,
    selectedFilteringYear,
    selectedFilteringMonth
  );
  const pastThreeMonthsRows = mapVisualsToRows(visuals, pastThreeMonthsPeriods);
  //
  const commonProps = {
    sx: {
      width: 260,
      '& .MuiInputBase-input': {
        padding: '6px 14px',
      },
    },
  };
  //
  const columns = [
    { field: 'name', headerName: 'Report Name', flex: 0.8, sortable: true },
    {
      field: 'description',
      headerName: 'Description',
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{params.value}</div>
      ),
    },
    {
      field: 'year',
      headerName: 'Year',
      flex: 0.3,
      sortable: false,
    },
    {
      field: 'month',
      headerName: 'Month',
      flex: 0.3,
      sortable: false,
    },
    {
      field: 'actions',
      headerName: 'Exports',
      align: 'center',
      headerAlign: 'center',
      flex: 0.5,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row">
          {FILE_FORMATS.map((f) => (
            <Tooltip key={f.type} title={`Export as ${f?.label}`}>
              <Button
                variant="contained"
                size="small"
                endIcon={<FileDownloadIcon fontSize="small" />}
                onClick={() => exportVisual(params.row)}
                sx={{
                  borderRadius: '16px',
                  fontSize: '0.60rem',
                  padding: '2px 8px',
                  minWidth: 'auto',
                }}
              >
                {f?.label}
              </Button>
            </Tooltip>
          ))}
        </Stack>
      ),
    },
  ];
  //
  return (
    active && (
      <Loader loading={loading}>
        <NoDataDialog
          open={openNoDataDialog}
          onClose={handleCloseNoDataDialog}
          message="No data available to export"
        />
        <Grid
          container
          direction="row"
          className={styles.mainGrid}
          sx={{
            display: value === TAB_VALUE.EXPORT_WORKBOOK_REPORT ? '' : 'none',
          }}
        >
          <Grid item xs={12} display="flex" justifyContent="center">
            <div style={{ width: '95%', minWidth: 700, marginBottom: 36 }}>
              <DataGrid
                autoHeight
                rows={pastThreeMonthsRows}
                columns={columns.map((col) => ({
                  ...col,
                  headerClassName: 'compact-header',
                  cellClassName: 'compact-cell',
                }))}
                getRowHeight={() => 'auto'}
                pageSize={10}
                disableSelectionOnClick
                hideFooterSelectedRowCount
                hideFooterPagination
                hideFooter
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 1,
                  '& .MuiDataGrid-cell': {
                    fontSize: '0.85rem',
                    padding: '4px 8px',
                  },
                  '& .MuiDataGrid-columnHeader': {
                    fontSize: '0.9rem',
                    backgroundColor: '#f5f5f5',
                  },
                  '& .MuiDataGrid-columnSeparator': {
                    display: 'none',
                  },
                }}
              />
            </div>
          </Grid>
        </Grid>

        <Grid
          container
          direction="row"
          className={styles.mainGrid}
          sx={{
            display: value === TAB_VALUE.EXPORT_WORKBOOK_REPORT ? '' : 'none',
          }}
        >
          <Grid item xs={12} display="flex" justifyContent="center" mt={4}>
            <div style={{ width: '95%', minWidth: 700 }}>
              <Typography variant="h4">Historical Search</Typography>{' '}
              <Stack direction="row" spacing={4} sx={{ mb: 4, mt: 2 }}>
                <Autocomplete
                  {...commonProps}
                  value={selectedFilteringReportName}
                  options={visuals?.map((r) => r.name)}
                  getOptionLabel={(option) => option.toString()}
                  onChange={(e, v) => {
                    setPage(0);
                    setSelectedFilteringReportName(v);
                  }}
                  renderInput={(p) => <TextField {...p} label="Report Name" size="small" />}
                />
                <Autocomplete
                  {...commonProps}
                  value={selectedFilteringYear}
                  options={years}
                  getOptionLabel={(option) => option.toString()}
                  onChange={(e, v) => {
                    setPage(0);
                    setSelectedFilteringYear(v);
                  }}
                  renderInput={(p) => <TextField {...p} label="Year" size="small" />}
                />
                <Autocomplete
                  {...commonProps}
                  value={selectedFilteringMonth}
                  options={MONTHS}
                  onChange={(e, v) => {
                    setPage(0);
                    setSelectedFilteringMonth(v);
                  }}
                  renderInput={(p) => <TextField {...p} label="Month" size="small" />}
                />
              </Stack>
              <DataGrid
                autoHeight
                rows={rows}
                columns={columns.map((col) => ({
                  ...col,
                  headerClassName: 'compact-header',
                  cellClassName: 'compact-cell',
                }))}
                getRowHeight={() => 'auto'}
                rowsPerPageOptions={[5, 10, 20]}
                disableSelectionOnClick
                hideFooterSelectedRowCount
                pageSize={pageSize}
                page={page}
                onPageChange={(pageNumber) => setPage(pageNumber)}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                sx={{
                  backgroundColor: 'white',
                  borderRadius: 1,
                  '& .MuiDataGrid-cell': {
                    fontSize: '0.85rem',
                    padding: '4px 8px',
                  },
                  '& .MuiDataGrid-columnHeader': {
                    fontSize: '0.9rem',
                    backgroundColor: '#f5f5f5',
                  },
                  '& .MuiDataGrid-columnSeparator': {
                    display: 'none',
                  },
                }}
              />
            </div>
          </Grid>
        </Grid>
      </Loader>
    )
  );
};
//
export default ExportWorkbookReport;
